import React, { useState } from 'react';
import {
  Box,
  Alert,
  Stack,
  Grid,
  alpha,
  useTheme,
} from '@mui/material';
import { SwapHoriz } from '@mui/icons-material';
import GlowButton from './shared/GlowButton';
import UtilityHeader from './shared/UtilityHeader';
import StyledTextArea from './shared/StyledTextArea';

const JsonUtility: React.FC = () => {
  const theme = useTheme();
  const [input, setInput] = useState(() => localStorage.getItem('json-input') || '');
  const [output, setOutput] = useState('');
  const [error, setError] = useState('');

  // Update localStorage when state changes
  React.useEffect(() => {
    localStorage.setItem('json-input', input);
  }, [input]);

  const formatJson = () => {
    try {
      const parsed = JSON.parse(input);
      const formatted = JSON.stringify(parsed, null, 2);
      setOutput(formatted);
      setError('');
    } catch (err) {
      setError('❌ Invalid JSON format');
    }
  };

  const minifyJson = () => {
    try {
      const parsed = JSON.parse(input);
      const minified = JSON.stringify(parsed);
      setOutput(minified);
      setError('');
    } catch (err) {
      setError('❌ Invalid JSON format');
    }
  };

  const validateJson = () => {
    try {
      JSON.parse(input);
      setOutput('✅ Valid JSON structure');
      setError('');
    } catch (err) {
      setError('❌ Invalid JSON: ' + (err as Error).message);
      setOutput('');
    }
  };

  const clearAll = () => {
    setInput('');
    setOutput('');
    setError('');
  };

  const swapInputOutput = () => {
    const temp = input;
    setInput(output);
    setOutput(temp);
    setError('');
  };

  return (
    <Box>
      <UtilityHeader 
        title="JSON Tool" 
        description="Format, minify, and validate JSON data with real-time feedback." 
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 3, border: '1px solid', borderColor: alpha(theme.palette.error.main, 0.3) }}>
          {error}
        </Alert>
      )}

      {output.startsWith('✅') && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 3, border: '1px solid', borderColor: alpha(theme.palette.success.main, 0.3) }}>
          {output}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <StyledTextArea
            label="Input"
            rows={14}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder='Enter JSON data here...'
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <StyledTextArea
            label="Result"
            accentColor={output.startsWith('✅') ? 'success' : 'secondary'}
            rows={14}
            value={output}
            onChange={(e) => setOutput(e.target.value)}
            placeholder="Formatted JSON will appear here..."
            InputProps={{
              sx: { 
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.85rem',
                lineHeight: 1.6,
                bgcolor: 'rgba(0,0,0,0.4)',
                borderRadius: 3,
                color: output.startsWith('✅') ? 'success.main' : 'primary.main',
                '& fieldset': { borderColor: output && !output.startsWith('✅') ? 'primary.main' : 'rgba(255,255,255,0.1)' },
              }
            }}
          />
        </Grid>
      </Grid>

      <Stack direction="row" spacing={2} sx={{ mt: 4, flexWrap: 'wrap', gap: 2 }}>
        <GlowButton
          size="large"
          onClick={formatJson}
          disabled={!input.trim()}
          sx={{ px: 4 }}
        >
          Format
        </GlowButton>
        <GlowButton
          size="large"
          color="secondary"
          glowColor="secondary"
          onClick={minifyJson}
          disabled={!input.trim()}
          sx={{ px: 4 }}
        >
          Minify
        </GlowButton>
        <GlowButton
          variant="outlined"
          disableGlow
          color="primary"
          onClick={validateJson}
          disabled={!input.trim()}
          sx={{ px: 4 }}
        >
          Validate
        </GlowButton>
        <Box sx={{ flexGrow: 1 }} />
        <GlowButton
          variant="outlined"
          disableGlow
          onClick={swapInputOutput}
          disabled={!input.trim() && !output.trim()}
          startIcon={<SwapHoriz />}
        >
          Swap
        </GlowButton>
        <GlowButton
          variant="outlined"
          disableGlow
          color="inherit"
          onClick={clearAll}
          disabled={!input.trim() && !output.trim()}
          sx={{ opacity: 0.7, borderColor: 'rgba(255,255,255,0.2)' }}
        >
          Clear
        </GlowButton>
      </Stack>
    </Box>
  );
};

export default JsonUtility;
